const post = {
  slug: 'how-to-evaluate-a-youtube-niche',
  cluster: 'niches',
  eyebrow: 'Niches',
  title: 'How to check whether a niche is worth entering',
  description:
    'A concrete check you can run in twenty minutes with search results alone — no keyword tool required.',
  keywords: [
    'evaluate youtube niche', 'youtube niche research', 'is my niche saturated',
    'youtube competition analysis', 'niche demand', 'youtube keyword research',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/how-to-evaluate-a-youtube-niche.webp',
    alt: 'A results page annotated with subscriber counts beside view counts, highlighting a small channel with a high-performing video.',
  },
  lede:
    'The question is not "is there competition" — competition is evidence of demand. The question is whether a channel with no audience can get watched here. That is checkable in twenty minutes.',
  sections: [
    {
      heading: 'The signal you are looking for',
      body: [
        {
          type: 'para',
          text: 'One thing tells you most of what you need: **do small channels get large view counts in this topic?**',
        },
        {
          type: 'para',
          text: 'If a channel with 3,000 subscribers has a video with 200,000 views, the topic is being recommended on merit. Videos are winning because they are good, not because of who posted them. That is an opening.',
        },
        {
          type: 'para',
          text: 'If every high-performing video belongs to a channel with a million subscribers, the topic is being served by brand. Your equally good video will not be surfaced the same way, because the system has no reason yet to trust it.',
        },
      ],
    },
    {
      heading: 'The twenty-minute check',
      body: [
        {
          type: 'list',
          ordered: true,
          items: [
            'Search five specific phrases someone in this niche would actually type — not the field name, the question.',
            'Filter to the last twelve months. Old results tell you about a topic\'s past, not its present.',
            'For the top ten results of each, note view count against subscriber count.',
            'Count how many come from channels under roughly 50,000 subscribers.',
            'Open the two best-performing small-channel videos and watch the first minute.',
          ],
        },
        {
          type: 'para',
          text: 'Step five is the one people skip and it is the most informative. You are looking for what the successful video in this niche actually does — the format, the pacing, what is on screen. That is your benchmark, and it is more useful than any metric.',
        },
      ],
    },
    {
      heading: 'Reading the result',
      body: [
        {
          type: 'sub',
          text: 'Several small channels doing well',
        },
        {
          type: 'para',
          text: 'The good case. Demand exists and is not locked up. Enter.',
        },
        {
          type: 'sub',
          text: 'Only big channels, high view counts',
        },
        {
          type: 'para',
          text: 'Demand exists but access does not. Do not enter at this level of breadth — go narrower until you find a sub-topic the big channels only touch occasionally.',
        },
        {
          type: 'sub',
          text: 'Few videos, low view counts',
        },
        {
          type: 'para',
          text: 'Usually not an untapped goldmine. It is much more often a topic people are not looking for. Treat an empty niche as a warning rather than an opportunity unless you have specific reason to think demand exists and is unserved.',
        },
        {
          type: 'sub',
          text: 'Many videos, all with low view counts',
        },
        {
          type: 'para',
          text: 'The genuinely saturated case: lots of supply, little interest per unit. This is where "make it but better" tends not to work, because the ceiling is low regardless of quality.',
        },
      ],
    },
    {
      heading: 'What the tools do and do not tell you',
      body: [
        {
          type: 'para',
          text: 'Keyword tools give search volume, which is one input. Two limits worth knowing.',
        },
        {
          type: 'para',
          text: 'First, a large share of views on many channels come from suggested and browse surfaces rather than search — so search volume can badly understate a topic\'s real audience. [Search versus browse traffic](/blogs/youtube-search-vs-browse-traffic) covers the difference.',
        },
        {
          type: 'para',
          text: 'Second, volume says nothing about whether the demand is *reachable* by you. That is exactly what the small-channel check above measures, and no volume number substitutes for it.',
        },
        {
          type: 'callout',
          title: 'Be sceptical of round numbers',
          text: 'Volume estimates from third-party tools are modelled, not measured, and different tools disagree substantially on the same term. Use them for relative comparison between topics, not as absolute figures to plan against.',
        },
      ],
    },
    {
      heading: 'The check most people skip',
      body: [
        {
          type: 'para',
          text: 'After all of the above, ask what your video would show. If the honest answer is stock footage of a generic scene, the niche fails the visual test in [choosing a niche](/blogs/faceless-youtube-niches) no matter how good the demand looks.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'How many subscribers counts as a "small channel" for this test?',
      a: 'It is relative to the niche rather than a fixed number. In a huge topic, 50,000 subscribers is small. In a specialised one, 3,000 is normal. What matters is whether the channels winning are notably smaller than the category leaders.',
    },
    {
      q: 'What if the niche looks good but I am not interested in it?',
      a: 'Do not enter it. Sustainability is a hard constraint, not a preference — a niche you are bored by produces flat writing, and on a faceless channel the writing is the entire product.',
    },
  ],
  related: ['faceless-youtube-niches', 'youtube-niches-to-avoid', 'youtube-search-vs-browse-traffic'],
};

export default post;
